import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import haatzaLogo from '../../../assets/Images/haatzaSellerlogo.png';
import {
  HelpCircle, ChevronDown, ChevronUp, Mail, ExternalLink,
  Search, Plus, Lightbulb
} from 'lucide-react';
import './SellOnlinePage.css';

export default function SellOnlinePage() {
  const [activeSection, setActiveSection] = useState('register-seller');
  const [expandedCards, setExpandedCards] = useState({});

  const sectionsRef = useRef({});

  const sidebarItems = [
    { label: 'Register as a Haatza Seller', id: 'register-seller' },
    { label: 'List Products', id: 'list-products' },
    { label: 'Shipping & Order Delivery', id: 'shipping-delivery' },
    { label: 'Fulfilment by Haatza (FBH)', id: 'fbh-section' },
    { label: 'Non-Fulfillment by Haatza (NFBH)', id: 'nfbh-section' },
    { label: 'Receive Payment', id: 'receive-payment' },
    { label: 'Grow Your Business', id: 'grow-business' },
    { label: 'Seller Support Center', id: 'support-center' },
    { label: 'Download Haatza App', id: 'download-app' }
  ];

  // Scroll-spy scroll listener
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 180; // header offset
      let currentSection = 'register-seller';

      sidebarItems.forEach((item) => {
        const el = sectionsRef.current[item.id];
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY;
          if (scrollPosition >= top - 30) {
            currentSection = item.id;
          }
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSidebarClick = (id) => {
    setActiveSection(id);
    const targetElement = sectionsRef.current[id];
    if (targetElement) {
      const offset = 90;
      const elementPosition = targetElement.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const toggleExpand = (cardKey) => {
    setExpandedCards((prev) => ({ ...prev, [cardKey]: !prev[cardKey] }));
  };

  return (
    <div className="sell-online-wrapper sell-online-page">
      <Navbar />

      {/* SECTION 1: HERO */}
      <section className="sell-hero-section" id="sell-top">
        <div className="container hero-grid-2 sell-online-mobile-hero-container">
          <div className="hero-copy sell-online-mobile-hero-copy">
            <span className="eyebrow-text">Learn How to Sell Online</span>
            <h1 className="hero-heading">
              <span className="hero-heading-line1">Become a Haatza seller to start </span>
              <span className="hero-heading-line2"><span className="highlight-blue">selling your products online</span> at 0% </span>
              <span className="hero-heading-line3">commission</span>
            </h1>
            <div className="hero-callout-simple sell-online-mobile-gstin-row">
              <span className="new-badge-pill">NEW</span>
              <div className="gst-callout-text sell-online-mobile-gstin-title sell-online-700-750-gst-callout-text">
                <div className="gst-callout-line sell-online-mobile-gstin-line1 sell-online-700-750-gst-callout-line1">Don't have a <span className="gstin-highlight">GSTIN</span> or have a Composition <span className="gstin-highlight">GSTIN</span>?</div>
                <div className="gst-callout-line sell-online-mobile-gstin-line2 sell-online-700-750-gst-callout-line2">You can still sell on Haatza. Click <a href="/donothavegst" className="gst-callout-link">here</a> to know more.</div>
              </div>
            </div>
            <a href="/signin" style={{ textDecoration: 'none' }}>
              <button className="hero-cta-btn">Start Selling</button>
            </a>
          </div>

          <div className="hero-visual-container">
            <img
              src="https://static.wixstatic.com/media/73d0ef_ebf5fd21cf0e403b993c8560ab0ae080~mv2.jpg/v1/fill/w_774,h_649,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/sellonlinesss.jpg"
              alt="Woman seller working on laptop"
              className="hero-woman-img"
            />
          </div>
        </div>
      </section>

      {/* SECTION 2: STAT CARDS (5-across row) */}
      <section className="sell-stats-section">
        <div className="container">
          <div className="stats-row-5">
            <div className="stat-pill-card">
              <img src="https://static.wixstatic.com/media/fe689e_29e581a64ccb4decaeb0415ae3d6c9f3~mv2.png/v1/fill/w_83,h_83,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%207_edited.png" alt="" className="stat-icon-img sell-online-700-750-extra-icon" />

              <p className="stat-pill-text sell-online-700-750-extra-description">10 crore+ Haatza customers</p>
            </div>
            <div className="stat-pill-card">
              <img src="https://static.wixstatic.com/media/fe689e_40e20dbf7f7f4bff8c992a94923d3fab~mv2.png/v1/fill/w_83,h_80,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%209_edited.png" alt="" className="stat-icon-img sell-online-700-750-extra-icon" />

              <p className="stat-pill-text sell-online-700-750-extra-description">
                7* days secure &<br />regular payments
              </p>
            </div>
            <div className="stat-pill-card">
              <img src="https://static.wixstatic.com/media/fe689e_f88547cc5cf44cca946ebb954edd5091~mv2.png/v1/fill/w_83,h_83,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%209_edited.png" alt="" className="stat-icon-img sell-online-700-750-extra-icon" />

              <p className="stat-pill-text sell-online-700-750-extra-description">Low cost of doing business</p>
            </div>
            <div className="stat-pill-card">
              <img src="https://static.wixstatic.com/media/fe689e_b3eb8c76b12b4192b7f97eddf173e225~mv2.png/v1/fill/w_83,h_83,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%209_edited.png" alt="" className="stat-icon-img sell-online-700-750-extra-icon" />

              <p className="stat-pill-text sell-online-700-750-extra-description">One click Seller Support</p>
            </div>
            <div className="stat-pill-card">
              <img src="https://static.wixstatic.com/media/fe689e_d429acdb7d2d4481acf6ab91090d9918~mv2.png/v1/fill/w_83,h_83,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%209_edited.png" alt="" className="stat-icon-img sell-online-700-750-extra-icon" />
              <p className="stat-pill-text sell-online-700-750-extra-description">
                Access to The Mega<br />Haatza Days & more
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: WHY SUPPLIERS LOVE HAATZA */}
      <section className="why-suppliers-section">
        <div className="container">
          <div className="why-suppliers-grid">
            <div className="why-suppliers-copy">
              <h2 className="section-h2 sell-online-700-750-main-heading sell-online-700-750-supplier-section-heading">Why Suppliers Love <span className="highlight-blue">Haatza?</span></h2>
              <p className="section-intro-text sell-online-700-750-description sell-online-700-750-benefits-intro">
                All the benefits that come with selling on Haatza are designed to help you sell more, and make it easier to<br />
                grow your business.
              </p>

              <div className="benefits-cards-2x2 sell-online-750-1000-stats-grid">
                <div className="benefit-card sell-online-750-1000-stats-card sell-online-700-750-supplier-card">
                  <div className="benefit-card-header">
                    <img src="https://static.wixstatic.com/media/fe689e_2378ac6785324d6a87177ac4caf8f209~mv2.jpg/v1/fill/w_86,h_64,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-22%20at%209_39_edited.jpg" alt="" className="benefit-icon-img sell-online-750-1000-stats-image sell-online-700-750-benefit-icon sell-online-700-750-supplier-card-icon" />

                    <h4 className="benefit-title sell-online-750-1000-stats-title sell-online-700-750-benefit-heading sell-online-700-750-supplier-card-heading">0% Commission</h4>
                  </div>
                  <p className="benefit-desc sell-online-750-1000-stats-description sell-online-700-750-benefit-description sell-online-700-750-supplier-card-description">
                    Sell your products online at 0% commission and enjoy a hassle-free selling experience on Haatza.
                  </p>
                </div>

                <div className="benefit-card sell-online-750-1000-stats-card sell-online-700-750-supplier-card">
                  <div className="benefit-card-header">
                    <img src="https://static.wixstatic.com/media/fe689e_22ac253134704008aa268200c5ba3861~mv2.jpg/v1/fill/w_66,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/grow.jpg" alt="" className="benefit-icon-img sell-online-750-1000-stats-image sell-online-700-750-benefit-icon sell-online-700-750-supplier-card-icon" />

                    <h4 className="benefit-title sell-online-750-1000-stats-title sell-online-700-750-benefit-heading sell-online-700-750-supplier-card-heading">10 Crore+ Customer</h4>
                  </div>
                  <p className="benefit-desc sell-online-750-1000-stats-description sell-online-700-750-benefit-description sell-online-700-750-supplier-card-description">
                    Haatza is one of the largest and fastest growing online selling platforms in India with over 10 Crore+ customers.
                  </p>
                </div>

                <div className="benefit-card sell-online-750-1000-stats-card sell-online-700-750-supplier-card">
                  <div className="benefit-card-header">
                    <img src="https://static.wixstatic.com/media/fe689e_a0671e359d164bc99802a2681d6aef9e~mv2.jpg/v1/fill/w_68,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-25%20at%2011_06_edited.jpg" alt="" className="benefit-icon-img sell-online-750-1000-stats-image sell-online-700-750-benefit-icon sell-online-700-750-supplier-card-icon" />

                    <h4 className="benefit-title sell-online-750-1000-stats-title sell-online-700-750-benefit-heading sell-online-700-750-supplier-card-heading">24000+ Pincode Served</h4>
                  </div>
                  <p className="benefit-desc sell-online-750-1000-stats-description sell-online-700-750-benefit-description sell-online-700-750-supplier-card-description">
                    Receive orders from all over India and sell products online to crores of customers across 24000+ pincodes.
                  </p>
                </div>

                <div className="benefit-card sell-online-750-1000-stats-card sell-online-700-750-supplier-card">
                  <div className="benefit-card-header">
                    <img src="https://static.wixstatic.com/media/fe689e_d5bb552c01234e009f04df280329d121~mv2.jpg/v1/fill/w_86,h_59,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-25%20at%2011_09_edited.jpg" alt="" className="benefit-icon-img sell-online-750-1000-stats-image sell-online-700-750-benefit-icon sell-online-700-750-supplier-card-icon" />
                    <h4 className="benefit-title sell-online-750-1000-stats-title sell-online-700-750-benefit-heading sell-online-700-750-supplier-card-heading">Low Cost Delivery</h4>
                  </div>
                  <p className="benefit-desc sell-online-750-1000-stats-description sell-online-700-750-benefit-description sell-online-700-750-supplier-card-description">
                    Enjoy the lowest delivery cost across India with our logistics partners, and offer fast delivery to your customers.
                  </p>
                </div>
              </div>
            </div>

            <div className="why-suppliers-visual">
              <img
                src="https://static.wixstatic.com/media/73d0ef_95b8b92b7cc842fb9295e07cd0483588~mv2.jpg/v1/fill/w_484,h_703,fp_0.33_0.50,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/seelll.jpg"
                alt="Smartphone and shipping delivery boxes"
                className="phone-boxes-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* MAIN LAYOUT FOR SCROLL-SPY SECTIONS 4 - 9 */}
      <div className="sellonline-main-layout sellonline-no-sidebar">

        {/* Scrollable Content */}
        <main className="sellonline-content-container">

          {/* SECTION 4: REGISTER AS A SELLER */}
          <section
            className="register-seller-card shadow-sm border section-anchor"
            id="register-seller"
            ref={(el) => (sectionsRef.current['register-seller'] = el)}
          >
            <h2 className="section-h2 sell-online-mobile-heading-16 sell-online-700-750-main-heading sell-online-700-750-how-to-sell-heading">How to Sell Your Products Online?</h2>
            <h3 className="register-seller-title sell-online-mobile-heading-16 sell-online-mobile-main-heading sell-online-700-750-register-heading"><span className="sell-underline-blue">Reg</span>ister as a Haatza Seller</h3>
            <p className="section-intro-text sell-online-700-750-description sell-online-700-750-shipping-desc">
              Create an account on Haatza Seller and list the products you want to sell online. All you need for Haatza seller registration is an active bank account and your GSTIN number (for GST sellers) or Enrolment ID / UIN (for non-GST sellers) and you'll be on your way to joining 5 Lakh+ suppliers. On completing the registration, you will have access to the Haatza Supplier Panel. You can then start selling products online to crores of customers who shop on Haatza.
            </p>

            <div className="no-gstin-box">
              <h4 className="box-subheading sell-online-mobile-heading-16 sell-online-mobile-main-heading sell-online-750-1000-gstin-heading sell-online-700-750-gstin-heading">Don't have a GSTIN?</h4>
              <p className="box-text sell-online-700-750-gst-intro">Follow the steps below to generate for your online business.</p>

              <div className="gst-steps-row">
                <div className="gst-step-item">
                  <img src="https://static.wixstatic.com/media/fe689e_0c1b776928a4491d9309f91bf5ddd79f~mv2.jpg/v1/fill/w_113,h_109,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%209_48_edited.jpg" alt="" className="gst-icon-img sell-online-700-750-gst-icon" />

                  <p className="gst-step-text sell-online-mobile-gstin-step sell-online-700-750-gst-step">
                    <a href="https://www.gst.gov.in" target="_blank" rel="noopener noreferrer" className="gst-link-black">
                      Register / Login to<br />www.gst.gov.in <ExternalLink size={14} />
                    </a>
                  </p>
                </div>
                <div className="gst-step-item">
                  <img src="https://static.wixstatic.com/media/fe689e_f9c05074dd424ea98677e85ccffdad09~mv2.jpg/v1/fill/w_113,h_112,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%209_46_edited.jpg" alt="" className="gst-icon-img sell-online-700-750-gst-icon" />

                  <p className="gst-step-text sell-online-mobile-gstin-step sell-online-700-750-gst-step">Fill in the GST Enrolment<br />Application Form</p>
                </div>
                <div className="gst-step-item">
                  <img src="https://static.wixstatic.com/media/fe689e_4cff1cbaf15d4ab5b48c99965d449b9a~mv2.jpg/v1/fill/w_111,h_112,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%209_edited.jpg" alt="" className="gst-icon-img sell-online-700-750-gst-icon" />
                  <p className="gst-step-text sell-online-mobile-gstin-step sell-online-700-750-gst-step">Submit Enrolment<br />Application</p>
                </div>
              </div>
            </div>

            <p className="closing-note-text sell-online-700-750-category-description">
              Haatza offers a diverse range of over 3000+ categories where you can sell your products. These categories represent just a fraction of the wide variety available on Haatza, providing ample opportunities for sellers to showcase their products to a large customer base.
            </p>
          </section>

          {/* SECTION 5: LIST PRODUCTS */}
          <section
            className="list-products-fullwidth-section light-blue-bg section-anchor"
            id="list-products"
            ref={(el) => (sectionsRef.current['list-products'] = el)}
          >
            <div className="list-products-inner-container">
              <div className="list-products-grid">
                <div className="list-products-copy">
                  <h3 className="list-products-title sell-online-mobile-heading-16 sell-online-mobile-main-heading sell-online-700-750-main-heading sell-online-700-750-lp-heading"><span className="sell-underline-blue">List</span> Products</h3>
                  <p className="section-intro-text tight-intro-text sell-online-700-750-description sell-online-700-750-list-products-description">
                    Once registered, set up your product page to start selling online on Haatza. Upload your product catalog on the Haatza Seller Panel and you'll be all set to sell and grow your business online. Uploading catalogs on Haatza is very simple. You can either upload a single catalog or upload bulk catalogs through excel. Your product catalog gets live post 24 hours from the time of upload.
                  </p>
                </div>

                <div className="list-products-visual">
                  <img
                    src="https://static.wixstatic.com/media/73d0ef_0046cf1dae1140b0970cae5692fa0c0f~mv2.png/v1/fill/w_669,h_314,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-08-06%20at%2010_42_19%E2%80%AFAM.png"
                    alt="Product Listing Preview"
                    className="list-products-mockup-img"
                  />
                </div>
              </div>

              {/* PLAIN TIP LINE BELOW 2-COLUMN GRID */}
              <div className="plain-tip-line fullwidth-tip-line">
                <span className="tip-bulb-icon">💡</span>
                <span className="tip-plain-text sell-online-700-750-did-you-know sell-online-700-750-did-you-know-description">
                  Did you know that providing precise and comprehensive information about your product, along with clear and captivating images, can increase its visibility on our platform by up to 15%.
                </span>
              </div>

              <div className="sub-listing-info sub-listing-grid tight-sub-grid">
                <div className="sub-listing-copy">
                  <h4 className="sub-info-heading sell-online-mobile-heading-16 sell-online-mobile-main-heading sell-online-700-750-listing-heading sell-online-700-750-listing-on-haatza-heading">Listing on Haatza</h4>
                  <p className="sub-info-text sell-online-700-750-description sell-online-700-750-listing-description">
                    To sell your products on Haatza, you need to create a new listing for each product. Provide complete details, including product description, dimensions, features, images, and usage instructions to ensure accurate representation and better visibility. Once submitted, your listing will be reviewed and made live on the platform.
                  </p>
                </div>

                <div className="youtube-player-wrapper video-down-margin sell-online-700-750-youtube-wrapper">
                  <iframe
                    src="https://www.youtube.com/embed/XZKYAJTId6U"
                    title="How to List Products on Haatza"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="embed-iframe sell-online-700-750-youtube-video"
                  ></iframe>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 6: SHIPPING & ORDER DELIVERY */}
          <section
            className="shipping-delivery-card shadow-sm border section-anchor"
            id="shipping-delivery"
            ref={(el) => (sectionsRef.current['shipping-delivery'] = el)}
          >
            <h2 className="section-h2 sell-online-mobile-heading-16 sell-online-mobile-main-heading sell-online-700-750-main-heading sell-online-700-750-shipping-heading"><span className="sell-underline-blue">Ship</span>ping & Order Delivery</h2>
            <p className="section-intro-text sell-online-700-750-description sell-online-700-750-shipping-desc">
              With Haatza, you can enjoy easy and stress-free delivery of all your products. Once you receive an order for your product, you will get an email notification. You can also check the order update on the Haatza Seller panel. Haatza delivers your product at the lowest shipping cost across India. Our logistics partner picks up the product from your location and delivers it straight to the customer.
            </p>

            {/* FBH Sub-section */}
            <div className="sub-service-block" id="fbh-section" ref={(el) => (sectionsRef.current['fbh-section'] = el)}>
              <div className="fbh-grid sell-online-mobile-fulfillment-content">
                <div className="fbh-copy sell-online-mobile-fulfillment-copy">
                  <h4 className="fbh-title sell-online-mobile-fbh-heading sell-online-700-750-fbh-heading">Fulfilment by Haatza (FBH)</h4>
                  <p className="service-desc sell-online-mobile-fulfillment-desc sell-online-700-750-description sell-online-700-750-fbh-desc">
                    Eliminate worries about storage, packing, shipping, and delivery by leveraging Haatza's Fulfilment by Haatza (FBH) service. FBH offers a comprehensive solution that handles all your shipping needs under one roof. With FBH, you can entrust Haatza to efficiently manage the entire process, from storing your products to expertly packing and shipping them to customers. Enjoy a hassle-free experience and focus on growing your business while Haatza takes care of the logistics.
                  </p>

                  <ul className="bullet-feature-grid vertical-bullet-list sell-online-mobile-fulfillment-points sell-online-750-1000-feature-point">
                    <li className="sell-online-700-750-fbh-feature"><span className="check-bullet">•</span> Pick-up from seller location to Haatza warehouse</li>
                    <li className="sell-online-700-750-fbh-feature"><span className="check-bullet">•</span> H-Assured badge</li>
                    <li className="sell-online-700-750-fbh-feature"><span className="check-bullet">•</span> Faster delivery to customers</li>
                    <li className="sell-online-700-750-fbh-feature"><span className="check-bullet">•</span> Seamless order processing</li>
                    <li className="sell-online-700-750-fbh-feature"><span className="check-bullet">•</span> Customer returns handled end to end</li>
                    <li className="sell-online-700-750-fbh-feature"><span className="check-bullet">•</span> Warehouse space</li>
                    <li className="sell-online-700-750-fbh-feature"><span className="check-bullet">•</span> Quality selection recommendations</li>
                    <li className="sell-online-700-750-fbh-feature"><span className="check-bullet">•</span> Rigorous quality checks</li>
                    <li className="sell-online-700-750-fbh-feature"><span className="check-bullet">•</span> High-quality packaging materials</li>
                  </ul>
                </div>

                <div className="youtube-player-wrapper fbh-video-down">
                  <iframe
                    src="https://www.youtube.com/embed/AiTuAr0ciGo"
                    title="Fulfilment by Haatza"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="embed-iframe"
                  ></iframe>
                </div>
              </div>
            </div>

            {/* NFBH Sub-section */}
            <div className="sub-service-block border-t pt-6" id="nfbh-section" ref={(el) => (sectionsRef.current['nfbh-section'] = el)}>
              <h4 className="nfbh-title sell-online-mobile-nfbh-heading sell-online-700-750-nfbh-heading">Non-Fulfillment by Haatza (NFBH)</h4>
              <p className="service-desc sell-online-mobile-fulfillment-desc sell-online-700-750-description sell-online-700-750-nfbh-desc">
                With Non-Fulfillment by Haatza (NFBH), you can benefit from end-to-end delivery of your products directly from your location to the customer. In NFBH, it is the responsibility of the seller to ensure that the product is properly packed and ready for dispatch before the Haatza agent arrives at the seller's location. This service allows you to maintain control over the packaging process while leveraging Haatza's logistics network for efficient and reliable delivery.
              </p>

              <ul className="bullet-feature-grid sell-online-mobile-nfbh-points sell-online-750-1000-feature-point">
                <li className="sell-online-700-750-shipping-feature"><span className="check-bullet">•</span> Delivery to 24,000+ pin codes across India</li>
                <li className="sell-online-700-750-shipping-feature"><span className="check-bullet">•</span> Real-time tracking of your product</li>
                <li className="sell-online-700-750-shipping-feature"><span className="check-bullet">•</span> Support for customer returns</li>
                <li className="sell-online-700-750-shipping-feature"><span className="check-bullet">•</span> Logistics support from community warehouses available</li>
              </ul>
            </div>

            {/* Receive Payment Sub-section */}
            <div className="sub-service-block border-t pt-6" id="receive-payment" ref={(el) => (sectionsRef.current['receive-payment'] = el)}>
              <div className="receive-payment-grid">
                <div className="receive-payment-copy">
                  <h3 className="receive-payment-title sell-online-mobile-heading-16 sell-online-mobile-main-heading sell-online-750-1000-receive-payment-heading sell-online-700-750-main-heading sell-online-700-750-receive-payment-heading"><span className="sell-underline-blue">Rec</span>eive Payment</h3>
                  <p className="service-desc receive-payment-desc sell-online-mobile-receive-desc sell-online-750-1000-receive-payment-desc sell-online-700-750-description">
                    Payment is securely deposited directly in your bank account, we follow a 7-day payment cycle from order delivery, including Cash on Delivery orders. You can view your deposited balance and future payments on the Haatza Seller Panel.
                  </p>
                </div>

                <div className="youtube-player-wrapper">
                  <iframe
                    src="https://www.youtube.com/embed/UZ_AUtlAMC0"
                    title="How to Receive Settlements on Haatza"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="embed-iframe"
                  ></iframe>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 7: HOW TO GROW YOUR ONLINE SELLING BUSINESS */}
          <section
            className="grow-business-card shadow-sm border section-anchor sell-online-mobile-grow-card"
            id="grow-business"
            ref={(el) => (sectionsRef.current['grow-business'] = el)}
          >
            <div className="grow-split-grid">
              <div className="grow-left-copy">
                <h2 className="grow-section-heading sell-online-700-750-main-heading sell-online-700-750-grow-section-heading">
                  How to Grow Your Online Selling Business on <span className="highlight-blue">Haatza?</span>
                </h2>
                <p className="section-intro-text sell-online-mobile-download-desc sell-online-700-750-description sell-online-700-750-first-order-description">
                  After you get your first order, it is time to start growing your online selling business! Some factors that help you building your online business are:
                </p>
                <a href="/signin" style={{ textDecoration: 'none' }}>
                  <button className="hero-cta-btn">Start Selling</button>
                </a>
              </div>

              <div className="grow-accordion-grid sell-online-mobile-grow-grid">
                {/* Card 1 */}
                <div className="accordion-card sell-online-700-750-grow-card">
                  <div className="accordion-header" onClick={() => toggleExpand('card1')}>
                    <img src="https://static.wixstatic.com/media/fe689e_d6c1b7e4f74c4772a6f03114a9235b5e~mv2.jpg/v1/fill/w_50,h_50,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-23%20at%205_57_edited.jpg" alt="" className="card-emoji-img sell-online-700-750-grow-icon sell-online-700-750-grow-card-icon" />
                    <h4 className="card-heading sell-online-mobile-grow-heading sell-online-700-750-grow-heading sell-online-700-750-grow-card-heading">Advertisements</h4>
                  </div>
                  <p className="card-body-text sell-online-700-750-grow-description sell-online-700-750-grow-card-description">
                    You can promote your product catalog to reach more customers and increase your sales using Haatza Ads{!expandedCards['card1'] && '...'}
                    {expandedCards['card1'] && (
                      <span className="expanded-extra">. Currently, not available for sellers who don't have a Regular GSTIN.</span>
                    )}
                  </p>
                  <button onClick={() => toggleExpand('card1')} className="show-more-btn">
                    {expandedCards['card1'] ? 'Show Less' : 'Show More'}
                  </button>
                </div>

                {/* Card 2 */}
                <div className="accordion-card sell-online-700-750-grow-card">
                  <div className="accordion-header" onClick={() => toggleExpand('card2')}>
                    <img src="https://static.wixstatic.com/media/fe689e_fdf61b1e66bb434f92fd3e50692c5082~mv2.jpg/v1/fill/w_50,h_50,fp_0.81_0.46,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-23%20at%209_54_edited.jpg" alt="" className="card-emoji-img sell-online-700-750-grow-icon sell-online-700-750-grow-card-icon" />
                    <h4 className="card-heading sell-online-mobile-grow-heading sell-online-700-750-grow-heading sell-online-700-750-grow-card-heading">Product Recommendations</h4>
                  </div>
                  <p className="card-body-text sell-online-700-750-grow-description sell-online-700-750-grow-card-description">
                    Grow your business by selling the products recommended by Haatza. These recommendations{!expandedCards['card2'] && '...'}
                    {expandedCards['card2'] && (
                      <span className="expanded-extra"> are made using customer demand data from across the internet.</span>
                    )}
                  </p>
                  <button onClick={() => toggleExpand('card2')} className="show-more-btn">
                    {expandedCards['card2'] ? 'Show Less' : 'Show More'}
                  </button>
                </div>

                {/* Card 3 */}
                <div className="accordion-card sell-online-700-750-grow-card">
                  <div className="accordion-header" onClick={() => toggleExpand('card3')}>
                    <img src="https://static.wixstatic.com/media/fe689e_8760f38f7d1043bd86f1f35c5cd45193~mv2.jpg/v1/fill/w_50,h_50,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-23%20at%209_55_edited.jpg" alt="" className="card-emoji-img sell-online-700-750-grow-icon sell-online-700-750-grow-card-icon" />
                    <h4 className="card-heading sell-online-mobile-grow-heading sell-online-700-750-grow-heading sell-online-700-750-grow-card-heading">Price Recommendation</h4>
                  </div>
                  <p className="card-body-text sell-online-700-750-grow-description sell-online-700-750-grow-card-description">
                    Sell online and set the Haatza recommended competitive price to increase your sales and visibility{!expandedCards['card3'] && '...'}
                    {expandedCards['card3'] && (
                      <span className="expanded-extra"> by getting an edge over sellers across online platforms.</span>
                    )}
                  </p>
                  <button onClick={() => toggleExpand('card3')} className="show-more-btn">
                    {expandedCards['card3'] ? 'Show Less' : 'Show More'}
                  </button>
                </div>

                {/* Card 4 */}
                <div className="accordion-card sell-online-700-750-grow-card">
                  <div className="accordion-header" onClick={() => toggleExpand('card4')}>
                    <img src="https://static.wixstatic.com/media/fe689e_63f09e51fa414e0eb33c06de1a01fbf5~mv2.png/v1/fill/w_50,h_50,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%207_edited.png" alt="" className="card-emoji-img sell-online-700-750-grow-icon sell-online-700-750-grow-card-icon" />
                    <h4 className="card-heading sell-online-mobile-grow-heading sell-online-700-750-grow-heading sell-online-700-750-grow-card-heading">Quality Dashboard</h4>
                  </div>
                  <p className="card-body-text sell-online-700-750-grow-description sell-online-700-750-grow-card-description">
                    You can reduce returns with Quality Dashboard. It provides detailed insights into why customers are{!expandedCards['card4'] && '...'}
                    {expandedCards['card4'] && (
                      <span className="expanded-extra"> returning your products.</span>
                    )}
                  </p>
                  <button onClick={() => toggleExpand('card4')} className="show-more-btn">
                    {expandedCards['card4'] ? 'Show Less' : 'Show More'}
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 8: SELLER SUPPORT CENTER */}
          <section
            className="support-center-fullwidth-section light-blue-bg section-anchor"
            id="support-center"
            ref={(el) => (sectionsRef.current['support-center'] = el)}
          >
            <div className="support-center-inner-container sell-online-mobile-support-inner">
              <div className="support-badge-brand sell-online-700-750-support-title sell-online-700-750-support-center">
                <span className="brand-h">Haatza</span>
                <span className="brand-divider-pipe"></span>
                <div className="brand-sub-stacked">
                  <span className="brand-sub-top">Seller</span>
                  <span className="brand-sub-bottom">
                    <span className="brand-support-bold">Support</span> Center
                  </span>
                </div>
              </div>

              <div className="support-grid-2">
                <div className="support-copy">
                  <h2 className="section-h2 support-2row-heading sell-online-mobile-support-heading-247 sell-online-750-1000-support-subheading sell-online-700-750-support-heading">
                    Haatza Supplier Support<br />Available 24/7
                  </h2>
                </div>

                <div className="support-action-box support-action-box-independent sell-online-mobile-support-action-content">
                  <p className="support-box-text sell-online-700-750-support-description">
                    Haatza supplier support is available to solve all your doubts and issues before and after you start your online selling business.
                  </p>
                  <div className="email-link-with-icon">
                    <img
                      src="https://static.wixstatic.com/media/fe689e_95aad345b1974df184b72c160c176a36~mv2.png/v1/fill/w_76,h_75,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-25%20at%2011_edited.png"
                      alt="Support Mail Icon"
                      className="support-mail-img"
                    />
                    <p className="contact-email-link">
                      You can reach out to<br />
                      <a href="mailto:sales@haatza.com">sales@haatza.com</a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 9: DOWNLOAD HAATZA APP */}
          <section
            className="download-app-card shadow-sm border section-anchor"
            id="download-app"
            ref={(el) => (sectionsRef.current['download-app'] = el)}
          >
            <div className="download-app-grid sell-online-750-1000-download-grid">
              <div className="download-app-copy">
                <h2 className="section-h2">
                  Download <span className="highlight-blue">Haatza:</span> Sell Anywhere,<br />Anytime
                </h2>
                <p className="section-intro-text sell-online-mobile-download-desc sell-online-700-750-download-description">
                  Download Haatza today and unlock the power to sell anywhere, anytime! Our easy-to-use platform is designed for entrepreneurs who want to expand their reach and maximize sales. Join thousands of satisfied users who have transformed their businesses with Haatza. Get started now and take your selling strategy to new heights!
                </p>

                <div className="store-badges-row">
                  {/* Google Play */}
                  <a
                    href="https://play.google.com/store/apps/details?id=com.haatza.seller&hl=en_IN&pli=1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="store-btn"
                    aria-label="Get it on Google Play"
                  >
                    <svg viewBox="0 0 140 40" className="store-svg">
                      <rect width="140" height="40" rx="6" fill="#000000" />
                      {/* Official Google Play logo vector path */}
                      <g transform="translate(12, 9) scale(0.043)">
                        <path fill="#EA4335" d="M199.9 237.8 1.4 470.17c7.22 24.57 30.16 41.81 55.8 41.81 11.16 0 20.93-2.79 29.3-8.37l244.16-139.46L199.9 237.8z" />
                        <path fill="#FBBC04" d="m433.91 205.1-104.65-60-111.61 110.22 113.01 108.83 104.64-58.6c18.14-9.77 30.7-29.3 30.7-50.23-1.4-20.93-13.95-40.46-32.09-50.22z" />
                        <path fill="#34A853" d="M199.42 273.45 329.27 145.1 87.9 8.37C79.53 2.79 68.36 0 57.2 0 30.7 0 6.98 18.14 1.4 41.86l198.02 231.59z" />
                        <path fill="#4285F4" d="M1.39 41.86C0 46.04 0 51.63 0 57.2v397.64c0 5.57 0 9.76 1.4 15.34l216.27-214.86L1.39 41.86z" />
                      </g>
                      <text x="36" y="15" fill="#ffffff" fontSize="6.5" fontWeight="bold" fontFamily="sans-serif">GET IT ON</text>
                      <text x="36" y="28" fill="#ffffff" fontSize="10.5" fontWeight="bold" fontFamily="sans-serif">Google Play</text>
                    </svg>
                  </a>
                  {/* App Store */}
                  <a
                    href="https://apps.apple.com/in/app/haatza-seller-sell-online/id6741692569"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="store-btn"
                    aria-label="Download on the App Store"
                  >
                    <svg viewBox="0 0 140 40" className="store-svg">
                      <rect width="140" height="40" rx="6" fill="#000000" />
                      {/* Highly accurate official Apple logo vector path */}
                      <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.48C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.1 16.67C20.08 16.74 19.67 18.11 18.71 19.5M15.97 4.17C16.63 3.37 17.07 2.28 16.95 1C15.85 1.04 14.51 1.73 13.73 2.64C13.07 3.41 12.49 4.52 12.64 5.78C13.87 5.87 15.12 5.17 15.97 4.17Z" fill="#ffffff" transform="translate(10, 8) scale(0.9)" />
                      <text x="36" y="15" fill="#ffffff" fontSize="6.5" fontWeight="bold" fontFamily="sans-serif">Download on the</text>
                      <text x="36" y="28" fill="#ffffff" fontSize="10.5" fontWeight="bold" fontFamily="sans-serif">App Store</text>
                    </svg>
                  </a>
                </div>
              </div>

              {/* Mobile Phone Mockup: iPhone Screenshot */}
              <div className="phone-listings-visual">
                <img
                  src="https://static.wixstatic.com/media/fe689e_ec6387e8d1e040068612de8f23d8987a~mv2.png/v1/fill/w_261,h_566,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Simulator%20Screenshot%20-%20iPhone%2015%20Pro%20-%202025-01-17%20at%2013_00_15.png"
                  alt="Haatza Seller App"
                  className="download-app-phone-img"
                />
              </div>
            </div>
          </section>

        </main>
      </div>

      <Footer />
    </div>
  );
}
